import { useState } from "react";
import Reveal from "@/components/Reveal";
import { postNewsletter } from "@/lib/api";
import { Instagram, Facebook, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await postNewsletter(email);
      setEmail("");
      toast.success(res.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-teal text-cream relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-10 relative">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-cream/15">
            <div className="lg:col-span-5">
              <h3 className="font-display font-bold text-3xl md:text-4xl leading-tight">Sweet news,<br /><span className="italic text-gold">straight to your inbox.</span></h3>
              <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-8 max-w-md">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label htmlFor="newsletter-email" className="text-xs uppercase tracking-[0.25em] text-cream/60">Email address</label>
                    <input id="newsletter-email" type="email" required data-testid="newsletter-email-input"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="info@morellis.com"
                      className="w-full bg-transparent border-b border-cream/40 focus:border-gold focus:outline-none py-2 text-lg placeholder:text-cream/30 transition-colors duration-300" />
                  </div>
                  <button type="submit" data-testid="newsletter-submit-button" disabled={sending} aria-label="Subscribe"
                    className="w-12 h-12 shrink-0 rounded-full bg-gold text-teal-deep grid place-items-center hover:bg-cream transition-colors duration-300 active:scale-95 disabled:opacity-60">
                    <ArrowRight size={18} />
                  </button>
                </div>
                <label className="mt-4 flex items-start gap-3 cursor-pointer text-xs text-cream/70 leading-relaxed" htmlFor="newsletter-consent">
                  <input id="newsletter-consent" type="checkbox" required data-testid="newsletter-consent-checkbox"
                    className="mt-0.5 accent-[#C5A059]" />
                  I would like to receive occasional news and promotions from Morelli&rsquo;s. You can unsubscribe anytime — see our <a href="/privacy" className="underline hover:text-gold transition-colors">privacy notice</a>.
                </label>
              </form>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Opening Hours</h4>
              <ul className="text-sm text-cream/75 space-y-2">
                <li className="flex justify-between gap-6"><span>Monday – Sunday</span><span>8:00 – 17:00</span></li>
                <li className="flex justify-between gap-6"><span>Coffee & Patisserie</span><span>from 8:00</span></li>
                <li className="flex justify-between gap-6"><span>Sundaes & waffles</span><span>all day</span></li>
                <li className="flex justify-between gap-6"><span>Last orders</span><span>15 min before close</span></li>
                <li className="text-cream/50 text-xs italic pt-1">Bank holiday hours may vary — check our socials on the day.</li>
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h4 className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Contact</h4>
              <address className="not-italic text-sm text-cream/75 space-y-2">
                <p>14 Victoria Parade</p>
                <p>Broadstairs, Kent</p>
                <p>CT10 1QS</p>
                <p><a href="mailto:info@morellis.com" className="hover:text-gold transition-colors duration-300">info@morellis.com</a></p>
              </address>
            </div>

            <div className="lg:col-span-2">
              <h4 className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Follow Us</h4>
              <div className="flex gap-3">
                {[
                  { icon: Instagram, label: "Instagram", id: "social-instagram", href: "https://www.instagram.com/morellisbroadstairs/" },
                  { icon: Facebook, label: "Facebook", id: "social-facebook", href: "https://www.facebook.com/share/14mS6ihc348/?mibextid=wwXlfr" },
                  { icon: TikTokIcon, label: "TikTok", id: "social-tiktok", href: "https://www.tiktok.com/@morellis.broadstairs" },
                ].map((s) => (
                  <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={s.id}
                    className="w-10 h-10 rounded-full border border-cream/30 grid place-items-center hover:bg-gold hover:border-gold hover:text-teal-deep transition-colors duration-300">
                    <s.icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="flex items-center justify-center gap-4 py-8 border-b border-cream/15" data-testid="accreditation-badges">
            <div className="flex items-center gap-3.5 rounded-full border border-gold/30 bg-cream/5 pl-2 pr-6 py-2" data-testid="badge-ica">
              <img src="/ica-logo.webp" alt="The Ice Cream Alliance logo" className="w-11 h-11 rounded-full object-cover" loading="lazy" />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Proud Member</p>
                <p className="font-display italic text-sm text-cream/90 leading-tight">The Ice Cream Alliance</p>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-10 text-xs text-cream/50 tracking-wide">
          <p>&copy; {new Date().getFullYear()} Morelli&rsquo;s Ice Cream Parlour, Broadstairs. An iconic tradition since 1907. <a href="/privacy" data-testid="footer-privacy-link" className="underline hover:text-gold transition-colors">Privacy</a> · <a href="/cart-hire" data-testid="footer-cart-hire-link" className="underline hover:text-gold transition-colors">Cart Hire</a></p>
          <p className="uppercase tracking-[0.25em] flex items-center gap-4">
            Handcrafted on the Kent coast
            <a href="/staff" data-testid="footer-staff-link" aria-label="Staff access" title="Staff access" className="text-cream/35 hover:text-gold transition-colors duration-300">
              <KeyRound size={14} />
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
